import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { getAccessToken } from '../../../authUtils';
import { Modal, Btn, ErrorBlock, Spinner, COLORS, Icons, TH, TD } from '../../ui/crm-ui';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FileRow {
  name:        string;
  type:        string;
  parentGroup: string;
  department:  string;
  xpmCode:     string;
}

interface ValidatedRow {
  index:    number;
  raw:      FileRow;
  errors:   string[];
  warnings: string[];
  isEmpty:  boolean;
}

type ImportStatus = 'pending' | 'processing' | 'done' | 'skipped' | 'failed';

interface ImportRowResult {
  index:   number;
  label:   string;
  status:  ImportStatus;
  message: string;
}

interface Props {
  baseApiUrl: string;
  open:       boolean;
  onClose:    () => void;
  onComplete: () => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const EXPECTED_HEADERS = ['Name', 'Type', 'Parent Group', 'Department', 'XPM Code'];
const EXPECTED_NORM    = EXPECTED_HEADERS.map((h) => h.toLowerCase());

const TEMPLATE_ROWS = [
  ['Smith Holdings', 'corporate_group', '',               'Tax', 'SMH001'],
  ['Smith Family',   'family_group',    'Smith Holdings', 'Tax', 'SMF001'],
];

const STEP_LABELS = ['Upload', 'Preview', 'Import', 'Results'];

const VALID_TYPES = ['corporate_group', 'family_group'] as const;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function validateHeaders(headers: string[]): string | null {
  const norm = headers.map((h) => h.trim().toLowerCase());
  if (norm.length !== EXPECTED_NORM.length) {
    return `Expected exactly ${EXPECTED_NORM.length} columns (${EXPECTED_HEADERS.join(', ')}), but found ${norm.length}. Please download and use the official template.`;
  }
  const missing = EXPECTED_NORM.filter((e) => !norm.includes(e));
  if (missing.length > 0) {
    return `Missing column${missing.length > 1 ? 's' : ''}: "${missing.join('", "')}". Please download and use the official template.`;
  }
  return null;
}

function buildHeaderMap(headers: string[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const h of headers) map[h.trim().toLowerCase()] = h;
  return map;
}

function buildFileRow(raw: Record<string, string>, hm: Record<string, string>): FileRow {
  return {
    name:        String(raw[hm['name']]         ?? '').trim(),
    type:        String(raw[hm['type']]         ?? '').trim(),
    parentGroup: String(raw[hm['parent group']] ?? '').trim(),
    department:  String(raw[hm['department']]   ?? '').trim(),
    xpmCode:     String(raw[hm['xpm code']]     ?? '').trim(),
  };
}

function validateRow(row: FileRow, index: number, deptMap: Map<string, number>): ValidatedRow {
  const isEmpty = !row.name && !row.type && !row.parentGroup && !row.department && !row.xpmCode;
  const errors: string[]   = [];
  const warnings: string[] = [];

  if (!isEmpty) {
    if (!row.name)
      errors.push('Name is required');
    if (!VALID_TYPES.includes(row.type as typeof VALID_TYPES[number]))
      errors.push(`Type "${row.type || '(empty)'}" is invalid — must be "corporate_group" or "family_group"`);
    if (!row.department)
      errors.push('Department is required');
    else if (!deptMap.has(row.department.toLowerCase()))
      warnings.push(`Department "${row.department}" not found — will be created on import`);
  }

  return { index, raw: row, errors, warnings, isEmpty };
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StepIndicator({ current }: { current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
      {STEP_LABELS.map((label, i) => {
        const n      = i + 1;
        const done   = n < current;
        const active = n === current;
        return (
          <React.Fragment key={n}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                border: `2px solid ${active ? COLORS.blue : done ? COLORS.green : COLORS.border}`,
                background: done ? COLORS.green : active ? COLORS.blue : COLORS.white,
                color: (done || active) ? '#fff' : COLORS.mid,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, flexShrink: 0,
              }}>
                {done ? Icons.check('#fff') : n}
              </div>
              <span style={{ fontSize: 10, color: active ? COLORS.blue : COLORS.mid, fontWeight: active ? 700 : 400 }}>
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div style={{ width: 48, height: 2, marginBottom: 16, flexShrink: 0, background: n < current ? COLORS.green : COLORS.border }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SummaryPill({ count, label, color }: { count: number; label: string; color: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 20, background: COLORS.white, border: `1px solid ${COLORS.border}` }}>
      <span style={{ fontSize: 20, fontWeight: 700, color }}>{count}</span>
      <span style={{ fontSize: 12, color: COLORS.mid }}>{label}</span>
    </div>
  );
}

function ResultCard({ count, label, color, bg }: { count: number; label: string; color: string; bg: string }) {
  return (
    <div style={{ flex: 1, padding: '16px 18px', borderRadius: 12, background: bg, border: `1px solid ${color}40`, textAlign: 'center' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{count}</div>
      <div style={{ fontSize: 12, color, fontWeight: 600, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function RowStatusIcon({ status }: { status: ImportStatus }) {
  if (status === 'done')       return <span style={{ color: COLORS.green,  fontSize: 16, lineHeight: 1 }}>✓</span>;
  if (status === 'failed')     return <span style={{ color: COLORS.red,    fontSize: 16, lineHeight: 1 }}>✗</span>;
  if (status === 'skipped')    return <span style={{ color: COLORS.mid,    fontSize: 16, lineHeight: 1 }}>—</span>;
  if (status === 'processing') return <span style={{ color: COLORS.blue,   fontSize: 16, lineHeight: 1 }}>⋯</span>;
  return                              <span style={{ color: COLORS.border, fontSize: 16, lineHeight: 1 }}>·</span>;
}

function RowCard({ r }: { r: ImportRowResult }) {
  const bg =
    r.status === 'done'       ? COLORS.greenSoft :
    r.status === 'failed'     ? COLORS.redSoft   :
    r.status === 'processing' ? COLORS.blueSoft  :
    r.status === 'skipped'    ? COLORS.light     : COLORS.pageBg;
  const borderColor =
    r.status === 'done'       ? `${COLORS.green}40` :
    r.status === 'failed'     ? `${COLORS.red}40`   :
    r.status === 'processing' ? `${COLORS.blue}40`  : COLORS.border;
  const labelColor =
    r.status === 'done'       ? COLORS.green :
    r.status === 'failed'     ? COLORS.red   :
    r.status === 'processing' ? COLORS.blue  : COLORS.mid;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: bg, border: `1px solid ${borderColor}` }}>
      <RowStatusIcon status={r.status} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.dark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {r.label}
        </div>
        {r.message && (
          <div style={{ fontSize: 11, color: r.status === 'failed' ? COLORS.red : COLORS.mid, marginTop: 2 }}>
            {r.message}
          </div>
        )}
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: labelColor, flexShrink: 0 }}>
        {r.status === 'processing' ? 'Creating…' : r.status}
      </span>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

const ImportWizardModal: React.FC<Props> = ({ baseApiUrl, open, onClose, onComplete }) => {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // Step 1
  const [dragOver,    setDragOver]    = useState(false);
  const [fileName,    setFileName]    = useState('');
  const [parseError,  setParseError]  = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2
  const [validatedRows, setValidatedRows] = useState<ValidatedRow[]>([]);
  const [deptMap,       setDeptMap]       = useState<Map<string, number>>(new Map());
  const [deptLoading,   setDeptLoading]   = useState(false);
  const [deptError,     setDeptError]     = useState<string | null>(null);

  // Step 3 / 4
  const [results,   setResults]   = useState<ImportRowResult[]>([]);
  const [importing, setImporting] = useState(false);

  // Reset every time the modal opens
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setDragOver(false); setFileName(''); setParseError(null);
    setValidatedRows([]); setDeptMap(new Map()); setDeptLoading(false); setDeptError(null);
    setResults([]); setImporting(false);
  }, [open]);

  // ── Template download ──────────────────────────────────────────────────────

  async function downloadTemplate() {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('SD Import Template');
    ws.addRow(EXPECTED_HEADERS);
    TEMPLATE_ROWS.forEach((r) => ws.addRow(r));
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sd-client-import-template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  }

  // ── File parsing ───────────────────────────────────────────────────────────

  async function advanceToPreview(rawRows: Record<string, string>[], headers: string[]) {
    const hm       = buildHeaderMap(headers);
    const fileRows = rawRows.map((r) => buildFileRow(r, hm));
    setDeptLoading(true);
    setDeptError(null);
    setStep(2);
    try {
      const token      = await getAccessToken();
      const authHeader = token ? { Authorization: `Bearer ${token}` } : {};
      const res        = await axios.get<Array<{ id: number; name: string }>>(
        `${baseApiUrl}/api/options/departments`, { headers: authHeader },
      );
      const map = new Map(res.data.map((d) => [d.name.toLowerCase(), d.id]));
      setDeptMap(map);
      setValidatedRows(fileRows.map((row, i) => validateRow(row, i, map)));
    } catch (err) {
      setDeptError(`Could not load departments: ${(err as Error).message}`);
      // Still show rows — dept warnings won't appear but structural errors will
      setValidatedRows(fileRows.map((row, i) => validateRow(row, i, new Map())));
    } finally {
      setDeptLoading(false);
    }
  }

  function parseFile(file: File) {
    setParseError(null);
    setFileName(file.name);
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

    if (ext === 'csv') {
      Papa.parse<Record<string, string>>(file, {
        header:         true,
        skipEmptyLines: true,
        complete: (result) => {
          const headers = result.meta.fields ?? [];
          const err     = validateHeaders(headers);
          if (err) { setParseError(err); return; }
          void advanceToPreview(result.data, headers);
        },
        error: (err) => setParseError(err.message),
      });
    } else if (ext === 'xlsx') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const wb = new ExcelJS.Workbook();
          await wb.xlsx.load(e.target?.result as ArrayBuffer);
          const ws = wb.worksheets[0];
          if (!ws) { setParseError('No sheets found in spreadsheet.'); return; }
          const headerRow = ws.getRow(1).values as unknown[];
          const headers = (headerRow.slice(1) as unknown[]).map(v => String(v ?? ''));
          const err = validateHeaders(headers);
          if (err) { setParseError(err); return; }
          const rows: Record<string, string>[] = [];
          ws.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber === 1) return;
            const values = (row.values as unknown[]).slice(1);
            const record: Record<string, string> = {};
            headers.forEach((h, i) => { record[h] = String(values[i] ?? ''); });
            rows.push(record);
          });
          if (!rows.length) { setParseError('No rows found in spreadsheet.'); return; }
          void advanceToPreview(rows, headers);
        } catch (err) {
          setParseError((err as Error).message);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (ext === 'xls') {
      setParseError('Legacy .xls files are not supported. Please open the file in Excel and save it as .xlsx, then try again.');
    } else {
      setParseError('Unsupported file type. Please upload a .csv, .xlsx, or .xls file.');
    }
  }

  function onFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
    e.target.value = '';
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  }

  // ── Import logic ───────────────────────────────────────────────────────────

  async function runImport() {
    const token = await getAccessToken();
    if (!token) { setDeptError('Not authenticated. Please sign in and try again.'); return; }

    const hdrs       = { Authorization: `Bearer ${token}` };
    const activeRows = validatedRows.filter((r) => !r.isEmpty);

    setResults(activeRows.map((r) => ({
      index: r.index, label: r.raw.name || `Row ${r.index + 1}`,
      status: 'pending' as ImportStatus, message: '',
    })));
    setImporting(true);
    setStep(3);

    // Mutable local dept map — extended as new departments are created
    const localDeptMap = new Map(deptMap);
    // In-session cache: lowercase group name → id (for resolving parent corp groups)
    const groupCache = new Map<string, number>();

    function updateResult(index: number, patch: Partial<ImportRowResult>) {
      setResults((prev) => prev.map((r) => r.index === index ? { ...r, ...patch } : r));
    }

    async function ensureDept(name: string): Promise<number | null> {
      const key = name.toLowerCase();
      if (localDeptMap.has(key)) return localDeptMap.get(key)!;
      // Create if missing (ignore error — may already exist from a parallel run)
      try { await axios.post(`${baseApiUrl}/api/options/departments`, { name }, { headers: hdrs }); } catch { /* ignore */ }
      // Always re-fetch to get the canonical id
      try {
        const res   = await axios.get<Array<{ id: number; name: string }>>(`${baseApiUrl}/api/options/departments`, { headers: hdrs });
        const found = res.data.find((d) => d.name.toLowerCase() === key);
        if (found) { localDeptMap.set(key, found.id); return found.id; }
      } catch { /* ignore */ }
      return null;
    }

    async function resolveCorpGroup(name: string): Promise<number | null> {
      const key = name.toLowerCase();
      if (groupCache.has(key)) return groupCache.get(key)!;
      try {
        const res   = await axios.get<Array<{ id: number; name: string; type: string }>>(`${baseApiUrl}/api/groups`, { headers: hdrs });
        const found = res.data.find((g) => g.name.toLowerCase() === key && g.type === 'corporate_group');
        if (found) { groupCache.set(key, found.id); return found.id; }
      } catch { /* ignore */ }
      return null;
    }

    for (const row of activeRows) {
      updateResult(row.index, { status: 'processing' });
      try {
        const m = row.raw;

        // 1. Ensure department exists (create if unknown)
        const deptId = m.department ? await ensureDept(m.department) : null;

        // 2. Resolve parent corp group for family groups
        let parentId: number | null = null;
        if (m.type === 'family_group' && m.parentGroup) {
          parentId = await resolveCorpGroup(m.parentGroup);
          if (parentId === null) throw new Error(`Parent group "${m.parentGroup}" not found`);
        }

        // 3. Create the group
        const payload: Record<string, unknown> = { name: m.name, type: m.type };
        if (deptId)    payload.department_id = deptId;
        if (m.xpmCode) payload.xpm_code      = m.xpmCode;
        if (parentId)  payload.parent_id     = parentId;

        const res = await axios.post<{ id: number }>(`${baseApiUrl}/api/groups`, payload, { headers: hdrs });

        // Cache the new group so subsequent family_group rows can find it as a parent
        groupCache.set(m.name.toLowerCase(), res.data.id);
        updateResult(row.index, { status: 'done' });
      } catch (err: any) {
        if (err.response?.status === 409) {
          updateResult(row.index, { status: 'skipped', message: 'Already exists' });
        } else {
          const msg = err.response?.data?.error ?? err.message ?? 'Unknown error';
          updateResult(row.index, { status: 'failed', message: msg });
        }
      }
    }

    setImporting(false);
    setStep(4);
  }

  // ── Derived values ─────────────────────────────────────────────────────────

  const activeRows      = validatedRows.filter((r) => !r.isEmpty);
  const errorRows       = activeRows.filter((r) => r.errors.length > 0);
  const warningRows     = activeRows.filter((r) => r.warnings.length > 0 && r.errors.length === 0);
  const skippedEmpty    = validatedRows.filter((r) => r.isEmpty);

  const doneCount    = results.filter((r) => r.status === 'done').length;
  const skippedCount = results.filter((r) => r.status === 'skipped').length;
  const failedCount  = results.filter((r) => r.status === 'failed').length;
  const completedCount = results.filter((r) => r.status !== 'pending' && r.status !== 'processing').length;
  const progressPct  = results.length ? Math.round((completedCount / results.length) * 100) : 0;

  const canImport = !deptLoading && activeRows.length > 0 && errorRows.length === 0;

  // ── Footer ─────────────────────────────────────────────────────────────────

  function renderFooter(): React.ReactNode {
    if (step === 1) return <Btn onClick={onClose}>Cancel</Btn>;
    if (step === 2) return (
      <>
        <Btn onClick={() => setStep(1)} disabled={deptLoading}>Back</Btn>
        <Btn primary onClick={runImport} disabled={!canImport}>
          Import {activeRows.length > 0 ? `${activeRows.length} row${activeRows.length !== 1 ? 's' : ''}` : ''}
        </Btn>
      </>
    );
    if (step === 3) return null;
    if (step === 4) return <Btn primary onClick={onComplete}>Done</Btn>;
    return null;
  }

  const footer = renderFooter();

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Modal
      open={open}
      onClose={importing ? () => undefined : onClose}
      title="Import Client Groups"
      width={720}
      footer={footer ?? undefined}
    >
      <StepIndicator current={step} />

      {/* ── Step 1: Upload ── */}
      {step === 1 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Btn small onClick={downloadTemplate}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                {Icons.download(COLORS.blue)} Download Template
              </span>
            </Btn>
          </div>

          <div style={{ background: COLORS.blueSoft, border: `1px solid ${COLORS.blue}30`, borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: COLORS.dark, lineHeight: 1.6 }}>
            Use the official template above — upload a CSV or Excel file with your client groups and we'll walk you through the rest.
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? COLORS.blue : COLORS.border}`,
              borderRadius: 12, padding: '48px 24px', textAlign: 'center',
              cursor: 'pointer', background: dragOver ? COLORS.blueSoft : COLORS.pageBg,
              transition: 'all 0.15s',
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
            <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.dark, marginBottom: 6 }}>
              Drop your file here, or click to browse
            </div>
            <div style={{ fontSize: 12, color: COLORS.mid }}>Supports .csv, .xlsx, .xls</div>
            {fileName && (
              <div style={{ marginTop: 12, fontSize: 12, color: COLORS.blue, fontWeight: 600 }}>
                Selected: {fileName}
              </div>
            )}
          </div>

          <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileInput} style={{ display: 'none' }} />
          {parseError && <div style={{ marginTop: 12 }}><ErrorBlock message={parseError} /></div>}
        </div>
      )}

      {/* ── Step 2: Preview ── */}
      {step === 2 && (
        <div>
          {deptLoading && <Spinner label="Loading departments…" />}
          {deptError   && <div style={{ marginBottom: 12 }}><ErrorBlock message={deptError} /></div>}

          {!deptLoading && (
            <>
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <SummaryPill count={activeRows.length}  label="Rows"            color={COLORS.blue} />
                <SummaryPill count={warningRows.length} label="Warnings"        color={COLORS.orange} />
                <SummaryPill count={errorRows.length}   label="Errors"          color={COLORS.red} />
                <SummaryPill count={skippedEmpty.length} label="Skipped (empty)" color={COLORS.mid} />
              </div>

              {errorRows.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <ErrorBlock message={`${errorRows.length} row${errorRows.length !== 1 ? 's have' : ' has'} errors that must be fixed in the file before importing.`} />
                </div>
              )}

              {activeRows.length === 0 ? (
                <div style={{ padding: '32px 0', textAlign: 'center', color: COLORS.mid, fontSize: 13 }}>
                  No rows to import. Check that your file has data rows below the header.
                </div>
              ) : (
                <div style={{ borderRadius: 10, border: `1px solid ${COLORS.border}`, maxHeight: 380, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <TH style={{ fontSize: 10 }}>#</TH>
                        <TH style={{ fontSize: 10 }}>Name</TH>
                        <TH style={{ fontSize: 10 }}>Type</TH>
                        <TH style={{ fontSize: 10 }}>Parent Group</TH>
                        <TH style={{ fontSize: 10 }}>Department</TH>
                        <TH style={{ fontSize: 10 }}>XPM Code</TH>
                        <TH style={{ fontSize: 10 }}>Status</TH>
                      </tr>
                    </thead>
                    <tbody>
                      {validatedRows.map((row) => {
                        if (row.isEmpty) return null;
                        const hasError = row.errors.length > 0;
                        const hasWarn  = !hasError && row.warnings.length > 0;
                        return (
                          <tr key={row.index} style={{ background: hasError ? COLORS.redSoft : hasWarn ? COLORS.orangeSoft : 'transparent' }}>
                            <TD style={{ fontSize: 11, color: COLORS.mid }}>{row.index + 1}</TD>
                            <TD style={{ fontSize: 12, fontWeight: 600 }}>
                              {row.raw.name || <span style={{ color: COLORS.red }}>—</span>}
                            </TD>
                            <TD>
                              {VALID_TYPES.includes(row.raw.type as typeof VALID_TYPES[number]) ? (
                                <span style={{ fontFamily: 'monospace', fontSize: 11, background: COLORS.light, padding: '2px 6px', borderRadius: 4 }}>
                                  {row.raw.type}
                                </span>
                              ) : (
                                <span style={{ color: COLORS.red, fontSize: 11 }}>{row.raw.type || '(empty)'}</span>
                              )}
                            </TD>
                            <TD style={{ fontSize: 12, color: COLORS.mid }}>{row.raw.parentGroup || '—'}</TD>
                            <TD style={{ fontSize: 12 }}>
                              {hasWarn ? (
                                <span style={{ color: COLORS.orange }}>{row.raw.department} <span style={{ fontSize: 10 }}>*new</span></span>
                              ) : row.raw.department ? (
                                row.raw.department
                              ) : (
                                <span style={{ color: COLORS.red }}>—</span>
                              )}
                            </TD>
                            <TD style={{ fontSize: 12, color: COLORS.mid }}>{row.raw.xpmCode || '—'}</TD>
                            <TD>
                              {hasError ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {row.errors.map((e, i) => (
                                    <span key={i} style={{ color: COLORS.red, fontSize: 11 }}>{e}</span>
                                  ))}
                                </div>
                              ) : hasWarn ? (
                                <span style={{ color: COLORS.orange, fontSize: 11 }}>⚠ {row.warnings[0]}</span>
                              ) : (
                                <span style={{ color: COLORS.green, fontSize: 11, fontWeight: 600 }}>Ready</span>
                              )}
                            </TD>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Step 3: Importing ── */}
      {step === 3 && (
        <div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: COLORS.mid, marginBottom: 6 }}>
              <span>Processing {results.length} row{results.length !== 1 ? 's' : ''}…</span>
              <span>{progressPct}%</span>
            </div>
            <div style={{ height: 8, borderRadius: 4, background: COLORS.light, overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 4, background: COLORS.blue, width: `${progressPct}%`, transition: 'width 0.3s ease' }} />
            </div>
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {results.map((r) => <RowCard key={r.index} r={r} />)}
          </div>
        </div>
      )}

      {/* ── Step 4: Results ── */}
      {step === 4 && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <ResultCard count={doneCount}    label="Created" color={COLORS.green} bg={COLORS.greenSoft} />
            <ResultCard count={skippedCount} label="Skipped" color={COLORS.mid}   bg={COLORS.light} />
            <ResultCard count={failedCount}  label="Failed"  color={COLORS.red}   bg={COLORS.redSoft} />
          </div>
          <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {results.map((r) => <RowCard key={r.index} r={r} />)}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ImportWizardModal;
